import { useRef, useState, type CSSProperties, type PointerEvent } from "react";

/**
 * Omstokking av en liste med dra og slipp.
 *
 * PORTERT FRA APPENS SIDELISTE, `screens/menu/PagesPane.tsx`, og regnestykket
 * er det samme med vilje: flytter man en rad her, skal den oppføre seg som
 * den gjør der.
 *
 * ALT REGNES MOT LAYOUTEN SLIK DEN VAR DA DRAGET STARTET, aldri mot
 * `getBoundingClientRect` underveis. Under draget er radene flyttet med
 * `transform`, og da svarer rect-en med den flyttede posisjonen: den løftede
 * raden fikk `dy` lagt til to ganger, og forskyvningen regnet avstander
 * mellom rader som selv var i bevegelse. Verdiene påvirket sin egen neste
 * måling, og lista hoppet.
 */

interface Drag {
  readonly fra: number;
  readonly til: number;
  readonly dy: number;
}

export interface Omstokking {
  /** Indeksen som følger fingeren, eller null. */
  readonly holdt: number | null;
  /** Ref-tilbakekall på hver rad. Nøkkel, ikke indeks — se under. */
  settRad(nokkel: string, el: HTMLElement | null): void;
  /** Settes på håndtaket. */
  haandtak(i: number): {
    onPointerDown(e: PointerEvent<HTMLElement>): void;
    onPointerMove(e: PointerEvent<HTMLElement>): void;
    onPointerUp(): void;
    onPointerCancel(): void;
  };
  /** Settes på raden. */
  stil(i: number): CSSProperties;
}

export function useOmstokking(
  nokler: readonly string[],
  onFlyttet: (fra: number, til: number) => void,
): Omstokking {
  const [drag, setDrag] = useState<Drag | null>(null);
  const rader = useRef(new Map<string, HTMLElement>());
  const startY = useRef(0);
  const layout = useRef<readonly { top: number; hoyde: number }[]>([]);

  const settRad = (nokkel: string, el: HTMLElement | null): void => {
    /* Nøkkel og ikke indeks: indeksene bytter plass når lista stokkes om, og
       da kunne en rad slette oppslaget en annen nettopp hadde satt. */
    if (el === null) rader.current.delete(nokkel);
    else rader.current.set(nokkel, el);
  };

  const start = (e: PointerEvent<HTMLElement>, i: number): void => {
    e.preventDefault();
    e.stopPropagation();
    // Kaster uten aktiv peker. Uten vakt avbrøt den før `setDrag`, og raden
    // nektet å flytte seg uten et tegn.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ingen aktiv peker */ }
    startY.current = e.clientY;
    // Ingenting er flyttet ennå, så dette er den rene layouten.
    layout.current = nokler.map((n) => {
      const b = rader.current.get(n)?.getBoundingClientRect();
      return { top: b?.top ?? 0, hoyde: b?.height ?? 0 };
    });
    setDrag({ fra: i, til: i, dy: 0 });
  };

  const flytt = (e: PointerEvent<HTMLElement>): void => {
    if (drag === null) return;
    const base = layout.current[drag.fra];
    if (base === undefined) return;
    const dy = e.clientY - startY.current;
    const midt = base.top + base.hoyde / 2 + dy;

    let til = drag.fra;
    layout.current.forEach((g, i) => {
      const m = g.top + g.hoyde / 2;
      if (i < drag.fra && midt < m) til = Math.min(til, i);
      else if (i > drag.fra && midt > m) til = Math.max(til, i);
    });
    setDrag({ fra: drag.fra, til, dy });
  };

  const slutt = (): void => {
    if (drag === null) return;
    if (drag.fra !== drag.til) onFlyttet(drag.fra, drag.til);
    setDrag(null);
    layout.current = [];
  };

  const forskyv = (i: number): string => {
    if (drag === null || i === drag.fra) return "";
    const { fra, til } = drag;
    let plass = i;
    if (fra < til && i > fra && i <= til) plass = i - 1;
    else if (til < fra && i >= til && i < fra) plass = i + 1;
    if (plass === i) return "";
    const a = layout.current[i];
    const b = layout.current[plass];
    if (a === undefined || b === undefined) return "";
    return `translateY(${b.top - a.top}px)`;
  };

  return {
    holdt: drag?.fra ?? null,
    settRad,
    haandtak: (i) => ({
      onPointerDown: (e) => start(e, i),
      onPointerMove: flytt,
      onPointerUp: slutt,
      onPointerCancel: slutt,
    }),
    stil: (i) => {
      if (drag?.fra === i) {
        return { transform: `translateY(${drag.dy}px) scale(1.015)` };
      }
      const f = forskyv(i);
      return f === "" ? {} : { transform: f };
    },
  };
}
